/**
 * MergeCandidatesModal — review queue for ambiguous dedup collisions.
 *
 * When an incoming lead's phone matches one contact but its email matches a
 * DIFFERENT contact, crm.resolve_contact refuses to auto-merge and parks a row
 * in crm.merge_candidates (status='pending'). This modal lists those rows and
 * lets a recruiter resolve each one manually:
 *  - Merge: pick which of the two records SURVIVES (winner). The other is
 *    tombstoned via crm.merge_contacts (children re-pointed, audit row written).
 *    POST /merge-candidates/:id/merge { winner_id }.
 *  - Not a duplicate: the two are genuinely different people. The row is marked
 *    rejected and drops out of the pending count. POST /merge-candidates/:id/dismiss.
 *
 * onResolved(id) lets the parent decrement its banner count without a refetch.
 */
import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, ArrowRight, Loader2, X } from 'lucide-react';
import api from '../../services/api';
import { contactName } from '../types';
import { formatPhone, prettySlug, relTime } from '../lib';
import { Z } from './ui';

interface MergeContact {
  id: string;
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
  phone_e164: string | null;
  email: string | null;
  lifecycle_status: string | null;
  created_at: string | null;
}

export interface MergeCandidate {
  id: string;
  reason: string;
  match_details: Record<string, unknown> | null;
  created_at: string;
  contact: MergeContact | null;
  conflicting_contact: MergeContact | null;
}

interface Props {
  candidates: MergeCandidate[];
  onClose: () => void;
  onResolved: (id: string) => void;
}

function ContactCard({
  contact,
  selected,
  onSelect,
  disabled,
}: {
  contact: MergeContact | null;
  selected: boolean;
  onSelect: () => void;
  disabled: boolean;
}) {
  if (!contact) {
    return (
      <div className="min-w-0 flex-1 rounded-lg border border-dashed border-gray-300 p-3 text-sm text-gray-400 dark:border-gray-600">
        Contact no longer exists
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      aria-pressed={selected}
      className={`min-w-0 flex-1 rounded-lg border p-3 text-left transition-colors duration-150 disabled:opacity-50 ${
        selected
          ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500 dark:border-primary-400 dark:bg-primary-900/20'
          : 'border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700/50'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
          {contactName(contact) || 'Unnamed'}
        </span>
        {selected && (
          <span className="flex-shrink-0 rounded-full bg-primary-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
            Keep
          </span>
        )}
      </div>
      <dl className="mt-1.5 space-y-0.5 text-xs text-gray-600 dark:text-gray-400">
        <div className="truncate">{contact.phone_e164 ? formatPhone(contact.phone_e164) : '— no phone'}</div>
        <div className="truncate">{contact.email || '— no email'}</div>
        <div className="truncate">
          {contact.lifecycle_status ? prettySlug(contact.lifecycle_status) : '—'}
          {contact.created_at ? ` · added ${relTime(contact.created_at)}` : ''}
        </div>
      </dl>
    </button>
  );
}

function CandidateRow({
  cand,
  onResolved,
}: {
  cand: MergeCandidate;
  onResolved: (id: string) => void;
}) {
  // Default the winner to the older record (the one already in the system that
  // the new lead's phone OR email matched) — recruiters can flip it.
  const [winnerId, setWinnerId] = useState<string | null>(cand.contact?.id || null);
  const [busy, setBusy] = useState<null | 'merge' | 'dismiss'>(null);
  const [error, setError] = useState<string | null>(null);

  // A merge needs both records present. If one was hard-deleted after flagging,
  // the only valid action is to dismiss — don't let the user pick a dead winner.
  const canMerge = !!cand.contact && !!cand.conflicting_contact;

  const run = async (action: 'merge' | 'dismiss') => {
    if (busy) return;
    if (action === 'merge' && !winnerId) {
      setError('Pick which record to keep first.');
      return;
    }
    setBusy(action);
    setError(null);
    try {
      await api.post(
        `/api/crm/merge-candidates/${cand.id}/${action}`,
        action === 'merge' ? { winner_id: winnerId } : {},
      );
      onResolved(cand.id);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.response?.data?.detail || e.message || 'Action failed.');
      setBusy(null);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
      <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
        {cand.reason} · flagged {relTime(cand.created_at)}
      </p>
      <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
        <ContactCard
          contact={cand.contact}
          selected={canMerge && winnerId === cand.contact?.id}
          onSelect={() => setWinnerId(cand.contact?.id || null)}
          disabled={!!busy || !canMerge}
        />
        <ArrowRight className="hidden h-4 w-4 flex-shrink-0 self-center text-gray-400 sm:block" />
        <ContactCard
          contact={cand.conflicting_contact}
          selected={canMerge && winnerId === cand.conflicting_contact?.id}
          onSelect={() => setWinnerId(cand.conflicting_contact?.id || null)}
          disabled={!!busy || !canMerge}
        />
      </div>

      {!canMerge && (
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          One of these records no longer exists, so they can't be merged — dismiss this candidate instead.
        </p>
      )}

      {error && (
        <p role="alert" className="mt-2 text-xs text-red-600 dark:text-red-400">
          {error}
        </p>
      )}

      <div className="mt-3 flex flex-wrap justify-end gap-2">
        <button
          type="button"
          onClick={() => run('dismiss')}
          disabled={!!busy}
          className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
        >
          {busy === 'dismiss' && <Loader2 className="h-4 w-4 animate-spin" />}
          Not a duplicate
        </button>
        <button
          type="button"
          onClick={() => run('merge')}
          disabled={!!busy || !winnerId || !cand.contact || !cand.conflicting_contact}
          className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:opacity-50 sm:min-h-0"
        >
          {busy === 'merge' && <Loader2 className="h-4 w-4 animate-spin" />}
          Merge into kept record
        </button>
      </div>
    </div>
  );
}

export default function MergeCandidatesModal({ candidates, onClose, onResolved }: Props) {
  const dialogRef = useRef<HTMLDivElement>(null);

  // Honor aria-modal: close on Escape and move focus into the dialog on open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    dialogRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className={`fixed inset-0 ${Z.modalRaised} flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4`}
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-t-xl bg-white shadow-xl focus:outline-none dark:bg-gray-800 sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Review merge candidates"
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 p-5 dark:border-gray-700">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-500" />
            <div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Review merge candidates</h3>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                A lead's phone matched one contact while its email matched another. Pick which record to keep, or mark
                them as different people.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto p-5">
          {candidates.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              All clear — no merge candidates to review.
            </p>
          ) : (
            candidates.map((c) => <CandidateRow key={c.id} cand={c} onResolved={onResolved} />)
          )}
        </div>
      </div>
    </div>
  );
}
